import { useEffect, useState, useMemo } from 'react'
import { basketService, Basket } from '@/services/basketService'
import useAppConfigStore from '@/stores/appConfigStore'
import { DetailHeader } from '@/components/ui/DetailHeader'
import DataTable from '@/components/ui/DataTable'
import FilterBar from '@/components/ui/FilterBar'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { RefreshCw, ShoppingBag, CheckCircle, XCircle, Package } from 'lucide-react'

const BasketsPage = () => {
  const { systemConfig } = useAppConfigStore()
  const [baskets, setBaskets] = useState<Basket[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [activeFilters, setActiveFilters] = useState<any[]>([])
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0
  })

  const loadBaskets = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await basketService.getAll()
      if (response.success) {
        setBaskets(response.data)
        setPagination(prev => ({
          ...prev,
          total: response.data.length
        }))
      } else {
        setError('Échec du chargement des paniers')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Une erreur est survenue')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadBaskets()
    
    // Set up polling every 10 seconds to sync with cart changes
    const intervalId = setInterval(() => {
      loadBaskets()
    }, 10000) // 10 seconds
    
    return () => {
      clearInterval(intervalId)
    }
  }, [])

  const formatCurrency = (amount: number) => {
    const currencyCode = systemConfig?.default_currency || ''
    if (!currencyCode) return amount.toFixed(2)
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currencyCode
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const columns = useMemo(() => [
    {
      key: 'name',
      label: 'Nom',
      sortable: true,
      render: (value: string, row: Basket) => (
        <div className="font-medium">
          <div className="flex items-center">
            <ShoppingBag className="h-4 w-4 mr-2 text-blue-600" />
            {value}
          </div>
          {row.description && (
            <div className="text-sm text-gray-500 truncate max-w-xs">
              {row.description}
            </div>
          )}
        </div>
      )
    },
    {
      key: 'merchant_name',
      label: 'Commerçant',
      sortable: true,
      render: (value: string | null) => (
        <div className="text-sm">{value || 'N/D'}</div>
      )
    },
    {
      key: 'original_value',
      label: 'Prix original',
      sortable: true,
      render: (value: number) => (
        <div className="text-sm font-medium">{formatCurrency(value)}</div>
      )
    },
    {
      key: 'sale_price',
      label: 'Prix de vente',
      sortable: true,
      render: (value: number) => (
        <div className="text-sm font-medium text-green-600">{formatCurrency(value)}</div>
      )
    },
    {
      key: 'quantity_available',
      label: 'Quantité',
      sortable: true,
      render: (value: number) => (
        <div className="text-sm">
          <div className="flex items-center">
            <Package className="h-3 w-3 mr-1 text-gray-500" />
            {value}
          </div>
        </div>
      )
    },
    {
      key: 'created_at',
      label: 'Créé le',
      sortable: true,
      render: (value: string) => (
        <div className="text-sm">{formatDate(value)}</div>
      )
    },
    {
      key: 'is_active',
      label: 'Statut',
      sortable: true,
      render: (value: boolean) => (
        value ? (
          <Badge variant="default" className="inline-flex items-center bg-green-100 text-green-800 border-green-200">
            <CheckCircle className="h-3 w-3 mr-1" />
            Actif
          </Badge>
        ) : (
          <Badge variant="destructive" className="inline-flex items-center">
            <XCircle className="h-3 w-3 mr-1" />
            Inactif
          </Badge>
        )
      )
    }
  ], [systemConfig])

  const actions = useMemo(() => [
    {
      label: 'Voir',
      icon: <ShoppingBag className="h-4 w-4" />,
      onClick: (row: Basket) => console.log('View basket', row.basket_id)
    },
    {
      label: 'Modifier',
      icon: <Package className="h-4 w-4" />,
      onClick: (row: Basket) => console.log('Edit basket', row.basket_id)
    }
  ], [])

  const filterOptions = useMemo(() => [
    {
      key: 'is_active',
      label: 'Statut',
      type: 'select' as const,
      options: [
        { value: 'all', label: 'Tous les statuts' },
        { value: 'true', label: 'Actif' },
        { value: 'false', label: 'Inactif' }
      ]
    }
  ], [])

  const handleFilterChange = (newFilters: any[]) => {
    setActiveFilters(newFilters)
  }

  const handleSearch = (value: string) => {
    setSearchQuery(value)
  }

  const handlePageChange = (page: number) => {
    setPagination(prev => ({ ...prev, page }))
  }

  const handlePageSizeChange = (newSize: number) => {
    setPagination(prev => ({ ...prev, pageSize: newSize, page: 1 }))
  }

  const filteredData = useMemo(() => {
    let result = baskets

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      result = result.filter(basket => 
        basket.name.toLowerCase().includes(query) || 
        (basket.description?.toLowerCase() || '').includes(query) ||
        (basket.merchant_name?.toLowerCase() || '').includes(query)
      )
    }

    // Apply other filters
    activeFilters.forEach(filter => {
      if (filter.key === 'is_active' && filter.value !== 'all') {
        const isActive = filter.value === 'true'
        result = result.filter(basket => basket.is_active === isActive)
      }
    })

    // Update pagination total
    setPagination(prev => ({ ...prev, total: result.length }))

    return result
  }, [baskets, searchQuery, activeFilters])

  const paginatedData = useMemo(() => {
    const startIndex = (pagination.page - 1) * pagination.pageSize
    const endIndex = startIndex + pagination.pageSize
    return filteredData.slice(startIndex, endIndex)
  }, [filteredData, pagination.page, pagination.pageSize])

  const breadcrumbs = [
    { label: 'Tableau de bord', href: '/' },
    { label: 'Paniers', href: '/baskets' }
  ]

  const headerActions = [
    {
      label: 'Actualiser',
      icon: <RefreshCw className="h-4 w-4 mr-2" />,
      onClick: loadBaskets,
      variant: 'outline' as const,
      disabled: loading
    }
  ]

  const stats = useMemo(() => ({
    total: baskets.length,
    active: baskets.filter(b => b.is_active).length,
    totalValue: baskets.reduce((sum, b) => sum + b.sale_price, 0)
  }), [baskets])

  return (
    <div className="space-y-6">
      <DetailHeader
        title="Paniers"
        subtitle="Gérez les paniers disponibles sur la plateforme"
        breadcrumbs={breadcrumbs}
        actions={headerActions}
      />

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
          <div className="mt-4">
            <Button variant="outline" onClick={loadBaskets}>
              Réessayer
            </Button>
          </div>
        </Alert>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total paniers</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Paniers actifs</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.active}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Valeur totale</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(stats.totalValue)}</div>
          </CardContent>
        </Card>
      </div>

      <FilterBar
        searchPlaceholder="Rechercher par nom, description ou commerçant..."
        filters={filterOptions}
        onSearch={handleSearch}
        onFilterChange={handleFilterChange}
      />

      <DataTable
        columns={columns}
        data={paginatedData}
        actions={actions}
        loading={loading}
        emptyMessage="Aucun panier trouvé"
        pagination={{
          current: pagination.page,
          pageSize: pagination.pageSize,
          total: pagination.total,
          onPageChange: handlePageChange,
          onPageSizeChange: handlePageSizeChange
        }}
      />
    </div>
  )
}

export default BasketsPage
